import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCategories, createCategory, updateCategory, deleteCategory } from "../api/CategoryAPIs";
import { useNavigate } from "react-router-dom";
import { colors } from "../constants/colors";
import { API_BASE_URL_V2 } from "../api/URL_Header";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteCategoryId, setDeleteCategoryId] = useState(null); // New state for delete confirmation

  const { data: categoryData, isLoading, error } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (categoryData) {
      setCategories(
        categoryData.map((cat) => ({
          id: cat.id,
          name: cat.name,
          imageUrl: cat.image,
        }))
      );
    }
  }, [categoryData]);

  const handleImageChange = (e, setState, state) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setState({
          ...state,
          imageBase64: reader.result.split(",")[1],
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e, setState, state) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createCategory(newCategory);
      setCategories([...categories, { id: created.CategoryId, name: created.Name, imageUrl: created.ImageUrl }]);  // Updated
      setNewCategory(null);
    } catch (err) {
      console.error("Error creating category:", err);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updated = await updateCategory(editingCategory.id, editingCategory);
      setCategories(categories.map((cat) => (cat.id === updated.CategoryId ? { id: updated.CategoryId, name: updated.Name, imageUrl: updated.ImageUrl } : cat)));
      setEditingCategory(null);
    } catch (err) {
      console.error("Error updating category:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteCategoryId);
      setCategories(categories.filter((cat) => cat.id !== deleteCategoryId));
      setDeleteCategoryId(null); // Close the modal
    } catch (err) {
      console.error("Error deleting category:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold">إدارة الفئات</h1>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            العودة إلى الداشبورد
          </button>
        </div>
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            فشل في تحميل الفئات. يرجى المحاولة مرة أخرى.
          </div>
        )}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl md:text-2xl font-semibold">الفئات ({categories.length})</h2>
          <button
            onClick={() => setNewCategory({ name: "", imageBase64: "" })}
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
          >
            إضافة فئة جديدة
          </button>
        </div>
        {isLoading ? (
          <SkeletonLoader type="table" />
        ) : categories.length ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-white shadow-md rounded-lg">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 text-right font-semibold">ID</th>
                  <th className="p-3 text-right font-semibold">الاسم</th>
                  <th className="p-3 text-right font-semibold">الصورة</th>
                  <th className="p-3 text-right font-semibold">التصميمات</th>
                  <th className="p-3 text-right font-semibold">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id} className="border-b hover:bg-gray-100">
                    <td className="p-3 text-right">{cat.id}</td>
                    <td className="p-3 text-right">{cat.name}</td>
                    <td className="p-3 text-right">
                      {cat.imageUrl ? (
                        <img
                          src={`${API_BASE_URL_V2}${cat.imageUrl}`}
                          alt={cat.name}
                          className="h-18 w-16 object-cover rounded"
                        />
                      ) : (
                        <span>لا توجد صورة</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/categories/${cat.id}/designs`)}
                        className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg shadow"
                      >
                        عرض التصميمات
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            setEditingCategory({
                              id: cat.id,
                              name: cat.name,
                              imageUrl: cat.imageUrl,
                              imageBase64: "",
                            })
                          }
                          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow"
                        >
                          تعديل
                        </button>
                        <button
                          onClick={() => setDeleteCategoryId(cat.id)} // Trigger confirmation modal
                          className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg shadow"
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center">لا توجد فئات</p>
        )}
        {newCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                إضافة فئة جديدة
              </h2>
              <form onSubmit={handleCreate} dir="rtl">
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم الفئة</label>
                  <input
                    type="text"
                    name="name"
                    value={newCategory.name}
                    onChange={(e) => handleInputChange(e, setNewCategory, newCategory)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, setNewCategory, newCategory)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setNewCategory(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600"
                    style={{ backgroundColor: colors.orange }}
                  >
                    إضافة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {editingCategory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                تعديل الفئة #{editingCategory.id}
              </h2>
              <form onSubmit={handleUpdate} dir="rtl">
                {editingCategory.imageUrl && (
                  <div className="mb-4">
                    <label className="block text-right mb-2">الصورة الحالية</label>
                    <img
                      src={editingCategory.imageUrl}
                      alt="Current"
                      className="h-20 object-cover rounded"
                    />
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-right mb-2">اسم الفئة</label>
                  <input
                    type="text"
                    name="name"
                    value={editingCategory.name}
                    onChange={(e) => handleInputChange(e, setEditingCategory, editingCategory)}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-right mb-2">الصورة الجديدة (اختياري)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, setEditingCategory, editingCategory)}
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setEditingCategory(null)}
                    className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-600"
                    style={{ backgroundColor: colors.orange }}
                  >
                    حفظ
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {deleteCategoryId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4" dir="rtl">
                تأكيد الحذف
              </h2>
              <p className="mb-4 text-right">
                هل أنت متأكد من حذف الفئة رقم #{deleteCategoryId}؟ هذا الإجراء لا يمكن التراجع عنه.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => setDeleteCategoryId(null)}
                  className="bg-gray-300 text-black py-2 px-4 rounded hover:bg-gray-400"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleDelete}
                  className="bg-red-500 text-white py-2 px-4 rounded hover:bg-red-600"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;